import { formatCount, pluralCategory } from '@/i18n/format.ts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * The locale this file IS.
 *
 * An error catalogue entry takes its parameters and nothing else — `describeApiError` looks a
 * key up and calls it, with no formatter bound — so the two entries here that count something
 * name their own locale. That is not a hardcoded 'en' of the kind LOC-020 bans: it is this
 * file's identity, and the pt-BR sibling says 'pt-BR' in the same place for the same reason.
 */
const LOCALE = 'en'

const counted = (n: number, one: string, other: string): string =>
  `${formatCount(LOCALE, n)} ${pluralCategory(LOCALE, n) === 'one' ? one : other}`

/**
 * The shared skeleton of the four capacity refusals: what is held, against what is allowed.
 *
 * Three states, not two — at the limit, past it (an administrator lowered it), and short of
 * it but asked to take on several at once, which is what deleting a group does when it hands
 * everything it owned to somebody else.
 */
const standing = (held: number, adding: number, limit: number, one: string, other: string, pronoun: string): string => {
  if (held === limit) return `${counted(held, one, other)}, the most ${pronoun} can have.`
  if (held > limit) return `${counted(held, one, other)}, and can have at most ${limit}.`
  return `${counted(held, one, other)} and can have at most ${limit}, so ${adding} more would be too many.`
}

const BASE: [string, string] = ['knowledge base', 'knowledge bases']
const AGENT: [string, string] = ['agent', 'agents']

/** What the owner of the refused item is told to do about it. */
const RAISE = 'or ask an administrator to raise the limit.'
const ELSEWHERE = `Choose another owner, ${RAISE}`

/** What the sharing domain's refusals say, in English (the source of truth AND the type). */
export const sharing: Pick<
  ServerMessages,
  | 'sharing.request_not_found'
  | 'sharing.request_already_decided'
  | 'sharing.request_not_waiting'
  | 'sharing.request_not_opened'
  | 'sharing.request_changed_while_reading'
  | 'sharing.only_admin_can_decide'
  | 'sharing.only_admin_can_review'
  | 'sharing.owner_cannot_own'
  | 'sharing.your_base_limit_reached'
  | 'sharing.owner_base_limit_reached'
  | 'sharing.your_agent_limit_reached'
  | 'sharing.owner_agent_limit_reached'
> = {
  'sharing.request_not_found': () => 'There is no sharing request with that id.',
  // `satisfies` rather than a lookup with a fallback: a sixth status added to the contract
  // should fail the build here, not render a blank where the outcome was (LOC-018).
  'sharing.request_already_decided': ({ status }) =>
    (
      ({
        approved: 'This request was already approved.',
        declined: 'This request was already declined.',
        withdrawn: 'This request was already withdrawn.',
        superseded: 'This request was already superseded.',
        closed: 'This request was already closed.'
      }) satisfies Record<typeof status, string>
    )[status],
  'sharing.request_not_waiting': () => 'This request was decided a moment ago. Reload to see the outcome.',
  'sharing.request_not_opened': () => 'This request could not be opened. Try again.',
  'sharing.request_changed_while_reading': () =>
    'This request changed while you were reading it. Reload it and decide on what it asks now.',

  'sharing.only_admin_can_decide': () => 'Only an administrator can decide a sharing request.',
  'sharing.only_admin_can_review': () => 'Only an administrator can review sharing requests.',

  'sharing.owner_cannot_own': () => 'That account cannot own anything.',
  'sharing.your_base_limit_reached': ({ held, adding, limit }) =>
    `You already have ${standing(held, adding, limit, ...BASE, 'you')} Delete one you no longer need, ${RAISE}`,
  'sharing.owner_base_limit_reached': ({ owner, ownerKind, held, adding, limit }) =>
    `${owner} already has ${standing(held, adding, limit, ...BASE, ownerKind === 'group' ? 'it' : 'they')} ${ELSEWHERE}`,
  'sharing.your_agent_limit_reached': ({ held, adding, limit }) =>
    `You already have ${standing(held, adding, limit, ...AGENT, 'you')} Retire one you no longer use, ${RAISE}`,
  'sharing.owner_agent_limit_reached': ({ owner, ownerKind, held, adding, limit }) =>
    `${owner} already has ${standing(held, adding, limit, ...AGENT, ownerKind === 'group' ? 'it' : 'they')} ${ELSEWHERE}`
}
